<template>
  <!--
    Service tab — wired to the two /requests endpoints:
      • GET  /requests   → returns both the recent-request list (data) AND
                           the button catalogue (filter.types). Refetched
                           on mount, after each POST, and via the manual
                           refresh button. No background polling
                           (real-time wasn't desired).
      • POST /requests   → fire when the customer taps a button

    Types whose `requires_note=true` open a small modal first; the rest
    POST immediately with an optimistic insert in the recent list.
  -->
  <div class="px-3 pt-3">

    <!-- "Need help?" / call-staff section ----------------------------------
         Rendered from request-types where is_call_staff=true. There's
         usually exactly one (the default seed "Call Staff"); if a branch
         enables more, they stack as additional buttons in the same card.
         The button-area swap uses fade-swap so the pill skeleton eases
         into the real button instead of snapping. -->
    <div class="card border-0 shadow-sm rounded-3 mb-3 bg-warning bg-opacity-25">
      <div class="card-body p-3 text-center">
        <i class="fa-solid fa-bell-concierge fa-2x text-warning mb-2" />
        <h5 class="fw-bold mb-1">{{ $t('Need help') + '?' }}</h5>
        <p class="text-muted small mb-3">{{ $t('Tap to call a staff member to your table') }}</p>

        <transition name="fade-swap" mode="out-in">
          <!-- Skeleton while loading types -->
          <div v-if="loadingTypes" key="sk" class="d-flex justify-content-center gap-2">
            <span class="skeleton-shimmer rounded-pill" style="height: 40px; width: 140px;" />
          </div>

          <div v-else-if="typesError" key="err" class="text-muted small">
            <i class="fa-solid fa-circle-exclamation text-danger me-1" />{{ typesError }}
          </div>

          <div v-else-if="callStaffTypes.length === 0" key="empty" class="text-muted small">
            {{ $t('Staff call is currently unavailable') }}
          </div>

          <div v-else key="real" class="d-flex flex-wrap justify-content-center gap-2">
            <button
              v-for="t in callStaffTypes"
              :key="'cs-' + t.id"
              type="button"
              class="btn btn-warning fw-bold px-4 rounded-pill"
              :disabled="!!sending[t.id]"
              @click="onTypeTap(t, $event)"
            >
              <i v-if="sending[t.id]" class="fa-solid fa-spinner fa-spin me-2" />
              <i v-else :class="iconClass(t.icon)" class="me-2" />
              {{ $t(t.name) }}
            </button>
          </div>
        </transition>
      </div>
    </div>

    <!-- Quick requests --------------------------------------------------- -->
    <div class="mb-2 d-flex justify-content-between align-items-center">
      <h6 class="fw-bold mb-0">{{ $t('Quick requests') }}</h6>
      <span class="small text-muted">{{ $t('Staff prepares it before coming') }}</span>
    </div>

    <!-- Quick-request tile grid. Cross-fades skeleton ↔ real grid, and
         the real tiles enter with a per-index stagger so they ripple
         into place instead of all materialising at once. -->
    <transition name="fade-swap" mode="out-in">
      <!-- Types skeleton -->
      <div v-if="loadingTypes" key="sk" class="row g-2">
        <div v-for="i in 6" :key="'qsk-' + i" class="col-6 col-md-4">
          <div class="skeleton-shimmer rounded-3" style="height: 88px; width: 100%;" />
        </div>
      </div>

      <!-- Types fetch error -->
      <div v-else-if="typesError" key="err" class="text-center text-muted py-4">
        <i class="fa-solid fa-circle-exclamation fa-2x text-danger mb-2 opacity-75" />
        <p class="small mb-2">{{ typesError }}</p>
        <button class="btn btn-sm btn-theme text-white rounded-pill px-3" @click="fetchRequests(false)">
          <i class="fa-solid fa-rotate me-1" />{{ $t('Try again') }}
        </button>
      </div>

      <!-- Types grid -->
      <div v-else key="real" class="row g-2">
        <div
          v-for="(t, ti) in quickRequestTypes"
          :key="'qr-' + t.id"
          class="col-6 col-md-4 tile-enter"
          :style="{ '--enter-delay': (ti * 20) + 'ms' }"
        >
          <button
            type="button"
            class="btn btn-light border w-100 py-3 rounded-3 d-flex flex-column align-items-center"
            :disabled="!!sending[t.id]"
            @click="onTypeTap(t, $event)"
          >
            <i v-if="sending[t.id]" class="fa-solid fa-spinner fa-spin fa-lg text-success mb-2" />
            <i v-else-if="ackedRecently[t.id]" class="fa-solid fa-check fa-lg text-success mb-2" />
            <i v-else :class="iconClass(t.icon)" class="fa-lg text-theme mb-2" />
            <span class="small fw-bold text-center">{{ $t(t.name) }}</span>
          </button>
        </div>

        <div v-if="quickRequestTypes.length === 0" class="col-12 text-center text-muted py-3 small">
          {{ $t('No quick requests configured for this branch') }}
        </div>
      </div>
    </transition>

    <!-- Recent requests --------------------------------------------------- -->
    <div class="mt-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="fw-bold text-muted small text-uppercase mb-0">
          <i class="fa-solid fa-clock-rotate-left me-1" />{{ $t('Recent Requests') }}
        </h6>
        <button
          class="btn btn-sm btn-light rounded-pill px-3"
          :disabled="loadingRequests"
          @click="fetchRequests(false)"
        >
          <i :class="loadingRequests ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'" />
        </button>
      </div>

      <transition name="fade-swap" mode="out-in">
        <div v-if="loadingRequests && requests.length === 0" key="sk" class="d-flex flex-column gap-2">
          <div v-for="i in 3" :key="'rsk-' + i" class="card border-0 shadow-sm rounded-3">
            <div class="card-body p-2 d-flex align-items-center">
              <div class="skeleton-shimmer rounded-circle me-3" style="height: 24px; width: 24px;" />
              <div class="flex-grow-1">
                <div class="skeleton-shimmer rounded mb-1" style="height: 12px; width: 50%;" />
                <div class="skeleton-shimmer rounded" style="height: 10px; width: 30%;" />
              </div>
              <div class="skeleton-shimmer rounded-pill" style="height: 18px; width: 70px;" />
            </div>
          </div>
        </div>

        <div v-else-if="requests.length === 0" key="empty" class="text-center text-muted py-3 small">
          {{ $t('No requests yet') }}
        </div>

        <div v-else key="real">
        <div
          v-for="(req, ri) in requests"
          :key="'req-' + req.id"
          class="card border-0 shadow-sm rounded-3 mb-2 tile-enter"
          :class="{ 'opacity-75': req._optimistic }"
          :style="{ '--enter-delay': (ri * 25) + 'ms' }"
        >
          <div class="card-body p-2 d-flex align-items-center">
            <i :class="iconClass(req.type?.icon)" class="text-theme me-3" />
            <div class="flex-grow-1">
              <div class="fw-bold small">
                {{ $t(req.type?.name || 'Request') }}
                <span v-if="req.note" class="text-muted fw-normal">— {{ req.note }}</span>
              </div>
              <div class="text-muted" style="font-size: 0.7rem;">
                {{ formatTime(req.created_at) }}
              </div>
            </div>
            <span class="badge" :class="stateBadgeClass(req.state)">
              <i :class="stateIcon(req.state)" class="me-1" />{{ $t('_Request.'+req.state_text || stateLabel(req.state)) }}
            </span>
          </div>
        </div>
        </div>
      </transition>
    </div>

    <!-- Note modal (for requires_note=true) ------------------------------ -->
    <template v-if="noteModalType">
      <div class="offcanvas-backdrop fade show" @click="closeNoteModal" />
      <div
        class="position-fixed bottom-0 start-0 end-0 bg-white shadow-lg p-3"
        style="z-index: 1055; border-radius: 18px 18px 0 0;"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="fw-bold mb-0">
            <i :class="iconClass(noteModalType.icon)" class="text-theme me-2" />
            {{ $t(noteModalType.name) }}
          </h5>
          <button class="btn-close" @click="closeNoteModal" />
        </div>

        <textarea
          v-model="noteText"
          class="form-control mb-2"
          rows="3"
          maxlength="500"
          :placeholder="$t('Describe your request...')"
        />
        <div class="d-flex justify-content-between text-muted small mb-3">
          <span><i class="fa-solid fa-circle-info me-1" />{{ $t('Required for this request') }}</span>
          <span>{{ noteText.length }} / 500</span>
        </div>

        <button
          class="btn btn-theme text-white w-100 fw-bold rounded-3 py-2"
          :disabled="!noteText.trim() || sendingFromModal"
          @click="confirmNoteAndSend"
        >
          <i v-if="sendingFromModal" class="fa-solid fa-spinner fa-spin me-2" />
          <i v-else class="fa-solid fa-paper-plane me-2" />
          {{ $t('Send to staff') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { ServiceRequests, isServiceSessionInvalid } from '@/services/endpoint/services.service';

/**
 * Map the API's icon string identifiers (bell, water, rice, …) onto the
 * FontAwesome class we want to render. Unknown names fall back to a
 * neutral marker so the button is still tappable.
 */
const ICON_MAP = {
  bell:      'fa-solid fa-bell-concierge',
  water:     'fa-solid fa-glass-water',
  rice:      'fa-solid fa-bowl-rice',
  utensils:  'fa-solid fa-utensils',
  tissue:    'fa-solid fa-toilet-paper',
  chili:     'fa-solid fa-pepper-hot',
  snowflake: 'fa-solid fa-snowflake',
  plate:     'fa-solid fa-plate-wheat',
  warning:   'fa-solid fa-triangle-exclamation',
  note:      'fa-solid fa-pen-to-square',
};

export default {
  name: 'TabService',
  emits: ['notify', 'ready', 'session-invalid'],
  data() {
    return {
      // Button catalogue — arrives in `filter.types` on every GET /requests.
      types: [],
      loadingTypes: true,
      typesError: null,

      // recent requests
      requests: [],
      loadingRequests: true,

      // POST state
      sending: {},             // { [typeId]: true } while POST in flight
      ackedRecently: {},       // { [typeId]: true } briefly after success — flashes the ✓
      sendingFromModal: false,

      // requires_note flow
      noteModalType: null,     // the type currently in the note modal
      noteText: '',
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    callStaffTypes() {
      return this.types.filter(t => t.is_call_staff);
    },
    quickRequestTypes() {
      return this.types.filter(t => !t.is_call_staff);
    },
  },
  methods: {
    iconClass(name) {
      return ICON_MAP[name] || 'fa-solid fa-circle-question';
    },

    // --- recent requests list --------------------------------------------
    /**
     * GET /requests returns both the recent-request list AND the button
     * catalogue (under `filter.types`), so a single call refreshes both.
     *
     * @param {boolean} silent — keep the existing list visible while refetching
     *   (used after a successful POST so the optimistic card doesn't flash to
     *   skeleton); the manual refresh button passes `false` for first-load
     *   skeleton + a visible error toast.
     */
    async fetchRequests(silent = false) {
      if (!silent) this.loadingRequests = true;
      // Types skeleton only on first load — subsequent refreshes update
      // the buttons in place rather than flashing them to skeleton.
      const isInitialTypesLoad = this.types.length === 0;
      if (isInitialTypesLoad) {
        this.loadingTypes = true;
        this.typesError = null;
      }
      let sessionInvalid = false;
      try {
        const res = await ServiceRequests.list(this.token);
        // Server returns id desc; trust it as source of truth and replace
        // the local list (drops any leftover optimistic entries).
        this.requests = res.data?.data ?? [];
        this.types = res.data?.filter?.types ?? [];
        this.typesError = null;
      } catch (err) {
        if (isServiceSessionInvalid(err)) {
          sessionInvalid = true;
          this.$emit('session-invalid');
          return;
        }
        const msg = err?.response?.data?.message || err?.message || this.$t('Network error');
        // If types never loaded, surface the error inline so the user can retry.
        if (this.types.length === 0) this.typesError = msg;
        if (!silent) {
          this.$emit('notify', {
            message: err?.response?.data?.message || this.$t('Failed to load recent requests'),
            variant: 'error',
            icon: 'fa-solid fa-triangle-exclamation',
          });
        }
      } finally {
        this.loadingRequests = false;
        if (isInitialTypesLoad) this.loadingTypes = false;
        if (!sessionInvalid) this.$emit('ready');
      }
    },

    // --- POST flow --------------------------------------------------------
    onTypeTap(type, ev) {
      // Same touch-device "stuck hover/focus" guard as TabMenu's pills.
      ev?.currentTarget?.blur?.();
      if (type.requires_note) {
        this.openNoteModal(type);
        return;
      }
      this.send(type, null);
    },

    openNoteModal(type) {
      this.noteModalType = type;
      this.noteText = '';
    },
    closeNoteModal() {
      this.noteModalType = null;
      this.noteText = '';
      this.sendingFromModal = false;
    },
    async confirmNoteAndSend() {
      const note = this.noteText.trim();
      if (!note || !this.noteModalType) return;
      this.sendingFromModal = true;
      const type = this.noteModalType;
      const ok = await this.send(type, note);
      this.sendingFromModal = false;
      if (ok) this.closeNoteModal();
    },

    /**
     * POST /requests with an optimistic-insert + revert-on-failure.
     * Returns true on success so the note modal can close itself.
     */
    async send(type, note) {
      const tempId = '_tmp-' + Date.now();
      // Optimistic card at the top of the recent list so the customer sees
      // immediate feedback even before the server responds.
      this.requests = [{
        id: tempId,
        _optimistic: true,
        type: { id: type.id, code: type.code, name: type.name, icon: type.icon },
        note: note || '',
        state: 'pending',
        state_text: 'Pending',
        created_at: new Date().toISOString(),
      }, ...this.requests];

      this.sending = { ...this.sending, [type.id]: true };

      try {
        const payload = { service_request_type_id: type.id };
        if (note) payload.note = note;
        await ServiceRequests.create(this.token, payload);

        // Replace optimistic + any duplicates with authoritative server view.
        await this.fetchRequests(true);

        // Brief ✓ on the tapped tile so the customer feels the success.
        this.ackedRecently = { ...this.ackedRecently, [type.id]: true };
        setTimeout(() => {
          this.ackedRecently = { ...this.ackedRecently, [type.id]: false };
        }, 2500);

        this.$emit('notify', {
          message: this.$t('Staff has been notified') + ' — ' + this.$t(type.name),
          variant: 'success',
          icon: 'fa-solid fa-bell',
        });
        return true;
      } catch (err) {
        // Roll the optimistic card back out.
        this.requests = this.requests.filter(r => r.id !== tempId);
        if (isServiceSessionInvalid(err)) {
          this.$emit('session-invalid');
          return false;
        }
        this.$emit('notify', {
          message: err?.response?.data?.message || this.$t('Failed to send request'),
          variant: 'error',
          icon: 'fa-solid fa-triangle-exclamation',
        });
        return false;
      } finally {
        this.sending = { ...this.sending, [type.id]: false };
      }
    },

    // --- recent-list badge helpers ---------------------------------------
    stateBadgeClass(state) {
      return ({
        pending:      'bg-secondary bg-opacity-25 text-secondary',
        acknowledged: 'bg-success bg-opacity-25 text-success',
        completed:    'bg-info bg-opacity-25 text-info',
        cancelled:    'bg-danger bg-opacity-25 text-danger',
      })[state] || 'bg-secondary bg-opacity-25 text-secondary';
    },
    stateIcon(state) {
      return ({
        pending:      'fa-solid fa-hourglass-half',
        acknowledged: 'fa-solid fa-check',
        completed:    'fa-solid fa-check-double',
        cancelled:    'fa-solid fa-xmark',
      })[state] || 'fa-solid fa-circle';
    },
    stateLabel(state) {
      return ({
        pending:      'Waiting...',
        acknowledged: 'Acknowledged',
        completed:    'Done',
        cancelled:    'Cancelled',
      })[state] || state;
    },

    formatTime(iso) {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        // Locale-aware HH:MM in the user's timezone (API returns UTC ISO).
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch {
        return '';
      }
    },
  },
  mounted() {
    this.fetchRequests();
  },
};
</script>

<style scoped>
/* Warm, brand-tinted shimmer — same recipe across all customer service
   tabs so the loading visuals stay consistent and on-brand. */
.skeleton-shimmer {
  background: linear-gradient(100deg,
    #f4e6d8 0%,
    #fcefe4 42%,
    #fff9f3 50%,
    #fcefe4 58%,
    #f4e6d8 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.4s ease-in-out infinite;
  display: block;
}
@keyframes pos-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Short cross-fade so the swap doesn't visibly delay the real content. */
.fade-swap-enter-active,
.fade-swap-leave-active {
  transition: opacity 0.12s ease;
}
.fade-swap-enter-from,
.fade-swap-leave-to {
  opacity: 0;
}

/*
  Per-tile entry stagger. Tight timing (~0.2s each, ~20ms apart) so the
  grid feels alive but doesn't make the user wait through a long stagger.
*/
.tile-enter {
  animation: tile-enter-in 0.2s ease-out both;
  animation-delay: var(--enter-delay, 0ms);
}
@keyframes tile-enter-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
